import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardCommandKey as CmdIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useSettings } from '../../contexts/SettingsContext';
import { supabase, isSupabaseConfigured } from '../../supabaseClient';

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { userProfile, canAccessModule } = useAuth();
  const { selectedCompanyId, isAllCompaniesView, getCompanyName } = useCompany();
  const { isModuleVisible } = useSettings();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [employees, setEmployees] = useState([]);
  const inputRef = useRef(null);

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  const isManager = userProfile?.role === 'manager';

  // Keyboard shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const handleOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open-global-search', handleOpenEvent);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open-global-search', handleOpenEvent);
    };
  }, []);

  // Load employees for search
  useEffect(() => {
    if (!open || !isSupabaseConfigured()) return;
    const fetchEmployees = async () => {
      let empQuery = supabase
        .from('employees')
        .select('id, full_name, employee_id, company_id')
        .eq('status', 'active')
        .limit(50);
      if (!isAllCompaniesView() && selectedCompanyId) {
        empQuery = empQuery.eq('company_id', selectedCompanyId);
      }
      const { data, error } = await empQuery;
      if (!error) setEmployees(data || []);
    };
    fetchEmployees();
  }, [open, selectedCompanyId, isAllCompaniesView]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 50);
    }
  }, [open]);

  const menuItems = useMemo(() => {
    const items = [
      { text: 'Dashboard', path: '/', module: null, roles: ['employee', 'manager', 'finance', 'admin', 'super_admin'] },
      { text: 'My Payslip', path: '/my-payslip', module: 'mypayslip' },
      { text: 'Employee List', path: '/employees', module: 'employees' },
      { text: 'Payroll', path: '/payroll', module: 'payroll' },
      { text: 'Advances', path: '/advances', module: 'advances' },
      { text: 'Leave Management', path: '/leave', module: 'leave' },
      { text: 'Expense Claims', path: '/expense-claims', module: 'expense_claims' },
      { text: 'Petty Cash', path: '/petty-cash', module: 'petty_cash' },
      { text: 'Main Float', path: '/main-float', module: 'main_float' },
      { text: 'Company Expenses', path: '/company-expenses', module: 'company_expenses' },
      { text: 'Unallocated Receipts', path: '/unallocated-receipts', module: 'company_expenses' },
      { text: 'Allowances & Bonuses', path: '/allowances', roles: ['admin', 'super_admin'] },
      { text: 'Org Chart', path: '/org-chart', module: 'org_chart' },
      { text: 'Onboarding / Offboarding', path: '/onboarding', module: 'onboarding' },
      { text: 'Overtime Requests', path: '/overtime', module: 'overtime' },
      { text: 'Asset Management', path: '/assets', module: 'assets' },
      { text: 'Public Holidays', path: '/holidays', module: 'public_holidays' },
      { text: 'Reports', path: '/reports', module: 'reports' },
      { text: 'My Profile', path: '/profile', module: null, roles: ['employee', 'manager', 'finance', 'admin', 'super_admin'] },
    ];

    return items.filter((item) => {
      if (item.module && !isModuleVisible(item.module)) return false;
      if (item.module) return canAccessModule(item.module);
      if (item.roles) return userProfile && item.roles.includes(userProfile.role);
      return true;
    });
  }, [canAccessModule, isModuleVisible, userProfile]);

  const normalizedQuery = query.trim().toLowerCase();

  const filteredMenu = useMemo(() => {
    if (!normalizedQuery) return [];
    return menuItems.filter((item) => item.text.toLowerCase().includes(normalizedQuery));
  }, [menuItems, normalizedQuery]);

  const filteredEmployees = useMemo(() => {
    if (!normalizedQuery) return [];
    return employees.filter((emp) =>
      emp.full_name.toLowerCase().includes(normalizedQuery) ||
      emp.employee_id.toLowerCase().includes(normalizedQuery)
    );
  }, [employees, normalizedQuery]);

  const handleNavigate = (path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  const handleEmployeeClick = (emp) => {
    setOpen(false);
    setQuery('');
    if (isAdmin || isManager) {
      navigate(`/employees/${emp.id}`);
    } else {
      navigate('/employees');
    }
  };

  const hasResults = filteredMenu.length > 0 || filteredEmployees.length > 0;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { mt: 8, borderRadius: '10px' } }}
    >
      <DialogContent sx={{ p: 2 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Search menu items or employees..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <Chip
                icon={<CmdIcon fontSize="small" />}
                label="K"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
            ),
          }}
        />

        {!normalizedQuery && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Type to search pages and employees
            </Typography>
          </Box>
        )}

        {normalizedQuery && !hasResults && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No results found
            </Typography>
          </Box>
        )}

        {filteredMenu.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, mb: 1, display: 'block' }}>
              Pages
            </Typography>
            <List dense disablePadding>
              {filteredMenu.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton onClick={() => handleNavigate(item.path)}>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {filteredEmployees.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, mb: 1, display: 'block' }}>
              Employees
            </Typography>
            <List dense disablePadding>
              {filteredEmployees.map((emp) => (
                <ListItem key={emp.id} disablePadding>
                  <ListItemButton onClick={() => handleEmployeeClick(emp)}>
                    <ListItemText
                      primary={emp.full_name}
                      secondary={`${emp.employee_id}${isAllCompaniesView() ? ` · ${getCompanyName(emp.company_id)}` : ''}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
